import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingScreen } from '@mesa-labs/mesa-ui';
import { isRejected } from '@reduxjs/toolkit';

import { FeeScheme } from '@mesa-labs/mesa-api/dist/types';
import { useDispatch, useSelector } from '../../redux/hooks';
import { setupCognito, updateEmail, updateExternalVendorId } from '../../redux/slices/auth';
import {
  updateAvailablePrograms,
  updateCustomMsa, updateFeeScheme, updateForceDirectDebitAgreement, updateOnboardingId, updatePartnerId,
  updateProgramCode,
  updateRequireInstantAccountVerification,
  updateSkipAutoInstantPayoutStep,
} from '../../redux/slices/onboarding';
import { updateVendorField } from '../../redux/slices/vendor';
import { useLazyValidateOnboardingIdQuery } from '../../redux/api/vendor';

type AuthRouteProps = {
  children: React.ReactElement,
  redirectToSignIn?: boolean,
  saveQueryStringData?: boolean,
};

function AuthRoute({
  children,
  redirectToSignIn = true,
  saveQueryStringData = false,
}: AuthRouteProps): React.ReactElement {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const vendorId = useSelector((state) => state.auth.vendorId);
  const onboardingTask = useSelector((state) => state.onboarding.onboardingTask);
  const [ready, setReady] = useState(false);

  const [validateOnboardingId] = useLazyValidateOnboardingIdQuery();

  useEffect(() => {
    (async () => {
      // save query string data if specified
      if (!saveQueryStringData) {
        return;
      }

      const searchParams = new URLSearchParams(window.location.search);
      const onboardingId = searchParams.get('onboardingId');

      // validate and update data from onboardingId, if applicable
      if (onboardingId) {
        const result = await validateOnboardingId({ onboardingId });

        if (result.isSuccess) {
          dispatch(updateOnboardingId(onboardingId));
          dispatch(updateExternalVendorId(result.data?.externalVendorId));
          dispatch(updatePartnerId(result.data?.partnerId));
          dispatch(updateFeeScheme(result.data?.feeScheme || FeeScheme.DISCOUNTING));
          dispatch(updateCustomMsa(result.data?.customMsa || false));
          dispatch(updateSkipAutoInstantPayoutStep(result.data?.skipAutoInstantPayoutStep || false));
          dispatch(updateRequireInstantAccountVerification(result.data?.requireInstantAccountVerification || false));
          dispatch(updateForceDirectDebitAgreement(result.data?.forceDirectDebitAgreement || false));

          if ((result.data?.availablePrograms?.length || 0) > 0) {
            const firstProgram = result.data?.availablePrograms?.[0];

            if (firstProgram?.programCode) {
              dispatch(updateProgramCode(firstProgram.programCode));
            }

            dispatch(updateAvailablePrograms(result.data?.availablePrograms));
          }

          dispatch(updateVendorField({ field: 'onboardingId', value: onboardingId }));
          dispatch(updateVendorField({ field: 'name', value: result.data?.vendorName }));
          dispatch(updateVendorField({ field: 'email', value: result.data?.email }));
        }
      }

      // update data from query strings and override onboarding data
      const email = searchParams.get('email');
      const businessName = searchParams.get('businessName');

      if (email) {
        dispatch(updateEmail(email));
        dispatch(updateVendorField({ field: 'email', value: email }));
      }

      if (businessName) {
        dispatch(updateVendorField({ field: 'name', value: businessName }));
      }
    })();
  }, [saveQueryStringData, dispatch, validateOnboardingId]);

  // auth setup and listeners
  useEffect(() => {
    (async () => {
      const result = await dispatch(setupCognito());

      if ((isRejected(result) || !(result.payload as Record<string, unknown>).email) && redirectToSignIn) {
        const pathname = window.location.pathname;

        navigate('/sign-in' + (pathname !== '/sign-in' ? '?redirect=' + encodeURIComponent(pathname) : ''), { replace: true });
        return;
      }

      setReady(true);
    })();
  }, [dispatch, navigate, redirectToSignIn]);

  useEffect(() => {
    if (!ready) {
      return;
    }

    if (!redirectToSignIn) {
      // vendor is considered "onboarded"
      if (isLoggedIn && vendorId) {
        // this is the effective post-confirmation redirect for the auto sign-in flow
        // (see the 'confirm_account' subscriber in redux/slices/auth.ts's signUp thunk,
        // whose own redirect() calls are inert outside a router loader/action)
        navigate(onboardingTask ? `/?onboardingTask=${onboardingTask}` : '/');
      } else if (isLoggedIn) {
        navigate('/onboarding/business-info');
      }

      return;
    }
  }, [
    ready,
    isLoggedIn,
    dispatch,
    navigate,
    redirectToSignIn,
    vendorId,
    onboardingTask,
  ]);

  return ready ? children : <LoadingScreen />;
}

export default AuthRoute;
